import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Table,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";

const ACCEPTABLE_DIM_TYPES = [
  "Angle",
  "Bulk Shaft",
  "Chamfer",
  "Check w/ Gauge*",
  "Copper Sulfate*",
  "Diameter",
  "Fillet",
  "Final Inspection*",
  "GD&T",
  "Heat Treat*",
  "Humidity Chamber*",
  "Lead Note",
  "Length",
  "Major Diameter",
  "Material Verification",
  "Minor Diameter",
  "Pass/Fail*",
  "Passivation*",
  "Radius",
  "Salt Fog*",
  "Spline",
  "Surface Finish",
  "Test",
  "Text Note",
  "Thread",
  "Title Note",
  "Torque (Cal)",
  "Weld*",
  "Weld Finish",
];

const METHOD_OPTIONS = [
  "X - Pass/Fail, Record in SSW Form",
  "Y - Pass/Fail, Record in Customer Approved Form",
  "A - Measure Variable, Record in SSW Form",
  "B - Measure Variable, Record in Customer Approved Form",
  " ",
];

const TOLERANCE_OPTIONS = [
  "Superfine",
  "Fine",
  "Medium",
  "Coarse",
  "Very Coarse",
  "100%",
  "First piece only",
  "Reference",
  "Programming Reference",
  "Gage Reference",
  "Basic Dimension",
  " ",
];

const COLUMN_DEFS = [
  { key: "DNA", label: "DNA", type: "input", width: 110 },
  { key: "DimensionType", label: "Dimension Type", type: "select", options: ACCEPTABLE_DIM_TYPES, width: 200 },
  { key: "Unit", label: "Unit", type: "input", width: 120 },
  { key: "Nominal", label: "Nominal", type: "input", width: 120 },
  { key: "Min", label: "Min", type: "input", width: 120 },
  { key: "Max", label: "Max", type: "input", width: 120 },
  { key: "ToleranceClass", label: "Tolerance Class", type: "select", options: TOLERANCE_OPTIONS, width: 190 },
  { key: "InProcess", label: "In Process", type: "input", width: 150 },
  { key: "MethodOfRecording", label: "Method Of Recording", type: "select", options: METHOD_OPTIONS, width: 240 },
  { key: "SpecialInstructions", label: "Special Instructions", type: "input", width: 220 },
  { key: "WIATPNumber", label: "WI or ATP Number", type: "input", width: 180 },
  { key: "Gages", label: "Gages", type: "datalist", width: 170 },
  { key: "AdditionalInfo", label: "Additional Info", type: "input", width: 240 },
  { key: "MOMOpNumber", label: "Mom Op #", type: "input", width: 140 },
];

function blankRow() {
  return {
    DNA: "",
    MOMOpNumber: "",
    DimensionType: "",
    Unit: "",
    Nominal: "",
    Min: "",
    Max: "",
    ToleranceClass: "",
    InProcess: "",
    MethodOfRecording: "",
    SpecialInstructions: "",
    WIATPNumber: "",
    Gages: "",
    AdditionalInfo: "",
  };
}

export default function EditPart({
  partNum = "",
  rev = 0,
  revMinor = 0,
  isImported = false,
  isNew = false,
  onBack,
  onOpenFrmSelect,
  onDone,
}) {
  const dialog = useAppDialog();
  const normalizedPartNum = String(partNum || "").trim().toUpperCase();

  const [rows, setRows] = useState([]);
  const [gageOptions, setGageOptions] = useState([" "]);
  const [childParts, setChildParts] = useState([]);
  const [childInput, setChildInput] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const [history, setHistory] = useState("");
  const [createdByLabel, setCreatedByLabel] = useState("");
  const [currentUsername, setCurrentUsername] = useState("");

  const [revMaj, setRevMaj] = useState(rev);
  const [revMin, setRevMin] = useState(revMinor);

  const [manualRevMaj, setManualRevMaj] = useState(String(rev));
  const [manualRevMin, setManualRevMin] = useState(String(revMinor));

  const [qtyForSampling, setQtyForSampling] = useState("");
  const [partText, setPartText] = useState(normalizedPartNum);
  const [keepOpenAfterSave, setKeepOpenAfterSave] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [lockOwner, setLockOwner] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [errorText, setErrorText] = useState("");

  const inputRefs = useRef({});
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const selectedRow = activeCell?.rowIndex != null ? rows[activeCell.rowIndex] : null;
  const selectedColumn = activeCell?.field || "";

  const historyLabel = isImported ? "" : history.trim() !== "" ? `History: ${history}` : "";
  const revisedByLabel = isImported ? "" : `Last revised by: ${createdByLabel}`;

  const filteredRowEntries = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const indexedRows = rows.map((row, index) => ({ row, index }));

    if (!q) return indexedRows;

    return indexedRows.filter(({ row }) =>
      Object.values(row).some((value) => String(value || "").toLowerCase().includes(q))
    );
  }, [rows, tableSearch]);

  const releaseLockIfOwned = useCallback(async () => {
    try {
      if (!normalizedPartNum || !currentUsername) return;

      const currentLockStatus = await getLockStatus({
        partNumber: normalizedPartNum,
        rev,
        revMinor,
      });

      if (
        String(currentLockStatus || "").trim().toLowerCase() ===
        currentUsername.trim().toLowerCase()
      ) {
        await setLockStatus({
          partNum: normalizedPartNum,
          rev,
          revMinor,
          newLock: "UNLOCK",
        });
      }
    } catch (error) {
      console.error("Cleanup failed:", error);
    }
  }, [normalizedPartNum, currentUsername, rev, revMinor]);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);

      if (!normalizedPartNum) {
        setRows([blankRow()]);
        return;
      }

      const username = await getCurrentUsername();
      if (!mountedRef.current) return;
      setCurrentUsername(username);

      const lockStatus = await getLockStatus({
        partNumber: normalizedPartNum,
        rev,
        revMinor,
      });
      if (!mountedRef.current) return;

      setLockOwner(lockStatus || "");

      if (
        (lockStatus || "").trim() !== "" &&
        (lockStatus || "").trim().toLowerCase() !== username.trim().toLowerCase()
      ) {
        await dialog.warning(`This part is already being checked by ${lockStatus}`);
        if (onBack) onBack();
        return;
      }

      await setLockStatus({
        partNum: normalizedPartNum,
        rev,
        revMinor,
        newLock: username.toLowerCase(),
      });

      const [oldHistory, revisedBy, partRows, gages, children] = await Promise.all([
        getOldHistory({ partNumber: normalizedPartNum, rev, revMinor }),
        getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor }),
        getPart({ partNumber: normalizedPartNum, rev, revMinor }),
        getGageList(),
        getChildren(normalizedPartNum),
      ]);

      if (!mountedRef.current) return;

      setHistory(oldHistory || "");
      setCreatedByLabel(revisedBy || "");
      setGageOptions([" ", ...(gages || [])]);
      setChildParts(children || []);

   const mappedRows = (partRows || []).map((r) => {
        const row = {
          DNA: stringOrEmpty(r.dna),
          MOMOpNumber: stringOrEmpty(r.momOpNumber),
          DimensionType: stringOrEmpty(r.dimensionType),
          Unit: stringOrEmpty(r.unit),
          Nominal: nullableDecimalToString(r.nominal),
          Min: nullableDecimalToString(r.min),
          Max: nullableDecimalToString(r.max),
          ToleranceClass: stringOrEmpty(r.toleranceClass),
          InProcess: stringOrEmpty(r.inProcess),
          MethodOfRecording: stringOrEmpty(r.methodOfRecording),
          SpecialInstructions: stringOrEmpty(r.specialInstructions),
          WIATPNumber: stringOrEmpty(r.wiatpNumber),
          Gages: stringOrEmpty(r.gages),
          AdditionalInfo: stringOrEmpty(r.additionalInfo),
        };

        if (row.DimensionType.toLowerCase().includes("note")) {
          row.AdditionalInfo = row.AdditionalInfo.toUpperCase();
        }

        return row;
    });

      setRows(mappedRows.length > 0 ? mappedRows : [blankRow()]);
    } catch (error) {
      console.error("Failed to load EditPart:", error);
      await dialog.error("Failed to load Edit Part page.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [normalizedPartNum, rev, revMinor, onBack, dialog]);

  useEffect(() => {
    mountedRef.current = true;
    loadPage();

    return () => {
      mountedRef.current = false;
    };
  }, [loadPage]);

  useEffect(() => {
    const beforeUnloadHandler = (e) => {
      if (isImported && !submitted) {
        e.preventDefault();
        e.returnValue =
          "You have not completed the import. Any existing work will be lost and a re-import will be required in the future.";
      }
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => window.removeEventListener("beforeunload", beforeUnloadHandler);
  }, [isImported, submitted]);

  useEffect(() => {
    return () => {
      releaseLockIfOwned();
    };
  }, [releaseLockIfOwned]);

  function setRowValue(index, field, value) {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      row[field] = value;

      if (field === "Gages" && value && !gageOptions.includes(value)) {
        setGageOptions((opts) => [...opts, value]);
      }

      if (field === "Gages" && value.trim().toLowerCase() === "pin gauge") {
        row.MethodOfRecording = "X - Pass/Fail, Record in SSW Form";
      }

      if (field === "DimensionType") {
        if (row.ToleranceClass.toLowerCase().includes("reference")) {
          row.AdditionalInfo = encodeReferenceSymbols(row.AdditionalInfo);
        }
      }

      if (field === "ToleranceClass") {
        if (value.toLowerCase().includes("reference")) {
          row.AdditionalInfo = encodeReferenceSymbols(row.AdditionalInfo);
        } else if (
          row.DimensionType.toLowerCase().includes("note") ||
          row.DimensionType.toLowerCase().includes("finish")
        ) {
          row.AdditionalInfo = decodeReferenceSymbols(row.AdditionalInfo);
        }
      }

      next[index] = row;
      return next;
    });

    setErrorText("");
  }

  function addBlankRow() {
    setRows((prev) => [...prev, blankRow()]);
  }

  function deleteCurrentRow() {
    if (!activeCell) return;
    setRows((prev) => prev.filter((_, idx) => idx !== activeCell.rowIndex));
    setActiveCell(null);
  }

  async function determineInProcess() {
    const qty = Number.parseInt(qtyForSampling.trim(), 10);
    if (Number.isNaN(qty)) {
      await dialog.warning("Please enter a valid sampling quantity.");
      return;
    }

    const nextRows = [...rows];

    for (let j = 0; j < nextRows.length; j++) {
      const row = { ...nextRows[j] };
      if (row.ToleranceClass && row.ToleranceClass.trim() !== "") {
        const multiplier = await determineMultiplier(
          row.DimensionType?.trim() || "",
          row.ToleranceClass?.trim() || "",
          qty
        );

        let result = 0;
        if (Number(multiplier) !== 0) {
          result = Math.ceil(qty * Number(multiplier));
        }

        if (result !== 0) {
          row.InProcess = `Every ${Math.floor(qty / result)}`;
        }

        nextRows[j] = row;
      }
    }

    setRows(nextRows);
  }

  async function copyCurrentRowToNewDNA() {
   const newDNANumber = await dialog.prompt({
      title: "Copy Row to New DNA",
      message:
        "Please enter the new DNA number to which this line will be copied to. If you change the DNA number afterwards, you may lose some information on the copied line.",
      placeholder: "Enter DNA number",
      confirmText: "Copy",
      cancelText: "Cancel",
      validateInput: (value) => {
        if (!value.trim()) return "DNA number is required.";
        return true;
      },
    });

    if (!newDNANumber) return;

    const normalized = newDNANumber.trim().toUpperCase();
    if (!normalized) return;

    for (const row of rows) {
      if (String(row.DNA || "").trim().toUpperCase() === normalized) {
        await dialog.warning(
          `DNA Number ${normalized} already exists. Please ensure you have a unique DNA number value.`
        );
        return;
      }
    }

    if (!activeCell || activeCell.rowIndex < 0 || activeCell.rowIndex >= rows.length) return;

    const source = rows[activeCell.rowIndex];
    const copy = { ...source, DNA: normalized };
    setRows((prev) => [...prev, copy]);
  }

  async function addChildPart() {
    const child = childInput.trim().toUpperCase();
    if (!child) return;

    const exists = await getPartExistsMajor(child);

    if (Number(exists) !== 0) {
      if (childParts.includes(child)) {
        await dialog.warning(
          "The child part that you have specified already exists in this list. Please double check your entry, and then try again. Thank you."
        );
      } else if (child === normalizedPartNum.trim().toUpperCase()) {
        await dialog.warning(
          "You cannot add a child part that is the same as the parent part. Operation Aborted."
        );
      } else {
        setChildParts((prev) => [...prev, child]);
        await dialog.success(`Child part ${child} has been successfully added.`);
        setChildInput("");
      }
    } else {
      await dialog.warning(
        "This part does not currently exist in the library of MAFIA tables. Please verify that this part exists before you can designate it as a child."
      );
    }
  }

  async function removeChildPart() {
    if (!childInput.trim()) return;
    const child = childInput.trim().toUpperCase();
    if (!childParts.includes(child)) return;

    const ans = await dialog.confirm({
      title: "Remove Child Part",
      message: `Are you sure you wish to remove child part ${child}?`,
      confirmText: "Remove",
      cancelText: "Cancel",
      type: "warning",
    });

    if (!ans) return;

    setChildParts((prev) => prev.filter((x) => x !== child));
    setChildInput("");
  }

  async function insertSymbol(symbolKind) {
    if (!activeCell) return;
    const { rowIndex, field } = activeCell;
    const row = rows[rowIndex];
    if (!row) return;

    const editableFields = ["AdditionalInfo", "SpecialInstructions", "WIATPNumber", "Gages", "Unit", "MOMOpNumber"];
    if (!editableFields.includes(field)) return;

    const key = `${rowIndex}-${field}`;
    const input = inputRefs.current[key];
    if (!input) return;

    const currentValue = row[field] || "";
    const start = input.selectionStart ?? currentValue.length;
    const end = input.selectionEnd ?? currentValue.length;

    const isReferenceStyle = String(row.ToleranceClass || "").toLowerCase().includes("reference");
    const mapping = getSymbolMapping(symbolKind, isNew, isReferenceStyle);
    if (!mapping.allowed) {
      await dialog.warning("This button is to provide a symbol for only 'Reference' type dimensions.");
      return;
    }

    const insertText = mapping.value;
    const next = currentValue.slice(0, start) + insertText + currentValue.slice(end);
    setRowValue(rowIndex, field, next);

    setTimeout(() => {
      const el = inputRefs.current[key];
      if (el) {
        el.focus();
        const pos = start + insertText.length;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  }

  async function validateDecimalFields(targetRows) {
    for (let j = 0; j < targetRows.length; j++) {
      const row = targetRows[j];
      for (const field of ["Max", "Min", "Nominal"]) {
        const val = String(row[field] ?? "").trim();
        if (val === "-" || val === "") continue;
        if (Number.isNaN(Number(val))) {
          const msg = `Non numeric ${field.toUpperCase()} value detected on row ${j + 1}. Please ensure Nominal, Min and Max values are either empty, or contain a numeric value only.`;
          setErrorText(msg);
          await dialog.warning(msg, "Invalid Numeric Value");
          return false;
        }
      }
    }
    return true;
  }

  async function validateRows(targetRows, fullValidation) {
    const dnaNumList = [];
    let problems = "";
    let opNumber = "";
    const dnaList = [];

    for (let j = 0; j < targetRows.length; j++) {
      const row = targetRows[j];
      const dna = String(row.DNA ?? "").trim();
      const dimType = String(row.DimensionType ?? "").trim();

      if (!dna || !dimType) {
        const msg = "Null values detected in DNA# or Dimension Type. Please make sure there are no empty rows in your MAFIA table and try again.";
        setErrorText(msg);
        await dialog.warning(msg);
        return { ok: false };
      }

      if (dnaNumList.includes(dna.toUpperCase())) {
        const msg = `There are duplicate DNA numbers in this MAFIA table. (DNA #${dna.toUpperCase()}.) Please correct the error and try again.`;
        setErrorText(msg);
        await dialog.warning(msg);
        return { ok: false };
      }
      dnaNumList.push(dna.toUpperCase());

      if (dna === "") {
        if (!problems.includes("Please ensure there is a DNA number for each dimension.")) {
          problems += " - Please ensure there is a DNA number for each dimension.\n";
        }
      } else if (dnaList.includes(dna.toLowerCase())) {
        problems += ` - Please ensure each DNA is a unique value. #${dna.toLowerCase()}\n`;
      } else {
        dnaList.push(dna.toLowerCase());
      }

      if (dimType === "") {
        if (!problems.includes(" - Please ensure there is a dimension type set for each dimension.")) {
          problems += " - Please ensure there is a dimension type set for each dimension.\n";
        }
      }

      if (!fullValidation) continue;

      const tolerance = String(row.ToleranceClass ?? "");
      const method = String(row.MethodOfRecording ?? "");
      const gage = String(row.Gages ?? "");
      const mom = String(row.MOMOpNumber ?? "");

      if (tolerance.trim() === "") {
        if (!dimType.toLowerCase().includes("note") && !dimType.toLowerCase().includes("basic") && !dimType.toLowerCase().includes("*")) {
          if (!problems.includes("Please ensure tolerance class is set for each dimension")) {
            problems += " - Please ensure tolerance class is set for each dimension. \n";
          }
        }
      }

      if (tolerance.trim() !== "" && !tolerance.toLowerCase().includes("reference") && !tolerance.toLowerCase().includes("basic")) {
        if (method.trim() === "") {
          if (!problems.includes(" - Please ensure each dimension has a 'Method of Recording' selected.")) {
            problems += " - Please ensure each dimension has a 'Method of Recording' selected.\n";
          }
        }

        if (!dimType.includes("*")) {
          if (gage.trim() === "") {
            if (!problems.includes(" - Please ensure a gage is selected for each dimension to be recorded.")) {
              problems += " - Please ensure a gage is selected for each dimension to be recorded.\n";
            }
          }
        }

        if (mom.trim() === "") {
          if (!opNumber.includes("MOM")) {
            opNumber += "MOM";
          }
        }
      }
    }

    if (problems !== "") {
      const msg = fullValidation
        ? `There is an error with this table. Please review the following error(s) before continuing.\n${problems}`
        : `There is an error with this table. Unfinished parts require at least a DNA Number and Dimension Type.\n${problems}`;
      setErrorText(msg);
      await dialog.warning(msg, "Validation Error");
      return { ok: false };
    }

    if (fullValidation && opNumber !== "") {
      const ans = await dialog.confirm({
        title: "Missing MOM Op #",
        message: "There is at least one DNA missing a MOM Op #, are you sure you wish to continue?",
        confirmText: "Continue",
        cancelText: "Cancel",
        type: "warning",
      });
      if (!ans) return { ok: false };
    }

    setErrorText("");
    return { ok: true };
  }

  async function persistRows({ targetPartNum, targetRevMaj, targetRevMin, historyToUse, createdByToUse, unfinished }) {
    for (let k = 0; k < rows.length; k++) {
      const row = rows[k];
        const payload = {
            Rev: targetRevMaj,
            RevMinor: targetRevMin,
            PartNum: stringOrEmpty(targetPartNum),
            DNANum: stringOrEmpty(row.DNA),
            DimType: stringOrEmpty(row.DimensionType),
            UnitMeasurement: stringOrEmpty(row.Unit),
            Nominal: stringOrEmpty(row.Nominal),
            Min: stringOrEmpty(row.Min),
            Max: stringOrEmpty(row.Max),
            ToleranceClass: stringOrEmpty(row.ToleranceClass),
            InProcess: stringOrEmpty(row.InProcess),
            MethodOfRecording: stringOrEmpty(row.MethodOfRecording),
            SpecialInstr: stringOrEmpty(row.SpecialInstructions),
            SpecialGages: stringOrEmpty(row.Gages),
            WIATPNum: stringOrEmpty(row.WIATPNumber),
            AddlInfo: stringOrEmpty(row.AdditionalInfo),
            MOMOperationNum:
              String(row.MOMOpNumber ?? "").trim() === ""
                ? null
                : Number.parseInt(String(row.MOMOpNumber).trim(), 10),
            createdBy: stringOrEmpty(createdByToUse),
            Stamp: new Date().toISOString(),
            History: stringOrEmpty(historyToUse),
          };

      const ok = unfinished ? await addLineUnfinished(payload) : await addLine(payload);
      if (!ok) {
        await dialog.error(
          `Error in adding Mafia Line to database. Please call technical support, x1776, PartNum = ${targetPartNum}`
        );
        return false;
      }
    }
    return true;
  }

  async function syncChildren(parentPartNum) {
    if (childParts.length > 0) {
      await deleteChild(parentPartNum);
      for (const child of childParts) {
        const ok = await addChildPartApi({ parentPart: parentPartNum, childPart: child });
        if (!ok) {
          await dialog.warning(
            "There seemed to be an error with the database and some (or all) of the children were not added. Please try again."
          );
        }
      }
    } else {
      await deleteChild(parentPartNum);
    }
  }

  function finalizeAndOpenFrmSelect(selectPartNum, selectRevMaj, selectRevMin, createdBy) {
    if (onOpenFrmSelect) {
      onOpenFrmSelect({ partnum: selectPartNum, revMajor: selectRevMaj, revMinor: selectRevMin, createdBy });
      return;
    }

    if (onDone) {
      onDone({ partnum: selectPartNum, revMajor: selectRevMaj, revMinor: selectRevMin, createdBy });
      return;
    }

    if (keepOpenAfterSave) {
      return;
    }

    if (onBack) onBack();
  }

  async function handleSaveAndClose() {
    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    let historyToUse = "";
    let createdByToUse = "";

    if (isImported) {
      historyToUse = "";
      createdByToUse = currentUsername.toLowerCase();
    } else if (isNew) {
      historyToUse = `(part created manually) ${currentUsername.toLowerCase()}`;
      createdByToUse = currentUsername.toLowerCase();
    } else {
      const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
      historyToUse = `${oldHistory}; (edit made) ${currentUsername.toLowerCase()}`;
      createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    }

    let targetRevMin = revMin;
    if (!isNew) targetRevMin = revMin + 1;

    const exists = await getPartExistsExact({ partNumber: normalizedPartNum, rev: revMaj, revMinor: targetRevMin });
    if (exists) {
      await dialog.warning(
        "Error -- this part already exists in the database. Someone must have created this part as the same time as you. Please save your work, investigate the issue, and come back when you are ready."
      );
      return;
    }

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: revMaj,
      targetRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(normalizedPartNum);

    const revisedByNow = await getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor });
    if (currentUsername.toLowerCase().trim() !== revisedByNow.toLowerCase().trim()) {
     // await sendEmailPartEdited({ fromName: currentUsername.toLowerCase(), partNum: normalizedPartNum, revMajor: rev, revMinor });
    }

    if (isImported) {
      await deleteMafiaTable(normalizedPartNum, revMaj, revMin);
      await updateRevMinor({ partNumber: normalizedPartNum, rev: revMaj, revMinor: revMin });
      targetRevMin = revMin;
    }

    finalizeAndOpenFrmSelect(normalizedPartNum.trim().toUpperCase(), revMaj, targetRevMin, currentUsername.toLowerCase());
  }

  async function handleSaveDashChild() {
    if (!partText.includes("-") || partText.trim().toUpperCase() === normalizedPartNum.trim().toUpperCase()) {
      await dialog.warning(
        "This button is for only saving a part with a dash number. Please use the 'Save and Close' button to continue without a dash number."
      );
      return;
    }

    let newPartNum = partText.trim();
    if (newPartNum.endsWith("-")) {
      newPartNum = newPartNum.slice(0, -1);
      setPartText(newPartNum);
    }

    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    const latestMajor = await getLatestMajorRev(newPartNum);
    const newRevMaj = Number(latestMajor) + 1;
    const latestMinor = await getLatestRevMinor({ partNum: newPartNum, rev: newRevMaj });
    const newRevMin = Number(latestMinor) + 1;

    const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    const historyToUse = `${oldHistory}; (child created) ${currentUsername.toLowerCase()}`;
    const createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });

    const ok = await persistRows({
      targetPartNum: newPartNum,
      targetRevMaj: newRevMaj,
      targetRevMin: newRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(newPartNum);
    finalizeAndOpenFrmSelect(newPartNum.trim().toUpperCase(), newRevMaj, newRevMin, createdByToUse);
  }

  async function handleManualRevChange() {
    const newRevMaj = Number.parseInt(manualRevMaj.trim(), 10);
    const newRevMin = Number.parseInt(manualRevMin.trim(), 10);

    if (Number.isNaN(newRevMaj) || Number.isNaN(newRevMin)) {
      await dialog.warning("Invalid Rev # detected, please try again.");
      return;
    }

    const tester = await getPart({ partNumber: normalizedPartNum, rev: newRevMaj, revMinor: newRevMin });
    if ((tester || []).length > 0) {
      await dialog.warning("A MAFIA table with the specified part and Rev #'s already exists. Operation aborted.");
      return;
    }

    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    const historyToUse = `${oldHistory}; (manual rev# change from ${revMaj}-${revMin} to ${newRevMaj}-${newRevMin}) ${currentUsername.toLowerCase()}`;
    const createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: newRevMaj,
      targetRevMin: newRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(normalizedPartNum);

    const revisedByNow = await getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor });
    if (currentUsername.toLowerCase().trim() !== revisedByNow.toLowerCase().trim()) {
      await sendEmailPartEdited({ fromName: currentUsername.toLowerCase(), partNum: normalizedPartNum, revMajor: rev, revMinor });
    }

    finalizeAndOpenFrmSelect(normalizedPartNum.trim().toUpperCase(), newRevMaj, newRevMin, createdByToUse);
  }

  async function handleSaveUnfinished() {
    const validation = await validateRows(rows, false);
    if (!validation.ok) return;

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: revMaj,
      targetRevMin: revMin + 1,
      historyToUse: "",
      createdByToUse: currentUsername.toLowerCase(),
      unfinished: true,
    });
    if (!ok) return;

    setSubmitted(true);
    await dialog.success("Unfinished part successfully saved to database.");

    if (onDone) {
      onDone({
        partnum: normalizedPartNum,
        revMajor: revMaj,
        revMinor: revMin + 1,
        createdBy: currentUsername.toLowerCase(),
        unfinished: true
      });
      return;
    }

    if (!keepOpenAfterSave && onBack) onBack();
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4 text-muted">Loading Edit Part workspace...</CardBody>
      </Card>
    );
  }

  return (
    <div className="edit-part-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="edit-part-toolbar-main">
                <div className="edit-part-toolbar-main-left">
                  <Button color="soft-dark" className="edit-part-action-btn" onClick={onBack}>
                    <i className="ri-arrow-left-line edit-part-btn-icon me-1" />
                    Back
                  </Button>
                </div>

                <div className="edit-part-toolbar-main-right">
                  <InputGroup className="edit-part-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      className="border-start-0"
                      placeholder="Search DNA, dimension, gage, note..."
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="edit-part-toolbar-utility">
                <Button color="primary" className="edit-part-action-btn edit-part-utility-btn" onClick={handleSaveAndClose}>
                  <i className="ri-save-line edit-part-btn-icon me-1" />
                  Save and Close
                </Button>

                <Button color="light" className="edit-part-action-btn edit-part-utility-btn border" onClick={handleSaveUnfinished}>
                  <i className="ri-draft-line edit-part-btn-icon me-1" />
                  Save Unfinished
                </Button>

                {!isImported && (
                  <>
                    <Button color="dark" className="edit-part-action-btn edit-part-utility-btn" onClick={handleManualRevChange}>
                      <i className="ri-git-branch-line edit-part-btn-icon me-1" />
                      Manual Rev Change Save
                    </Button>
                    <Button color="dark" className="edit-part-action-btn edit-part-utility-btn" onClick={handleSaveDashChild}>
                      <i className="ri-node-tree edit-part-btn-icon me-1" />
                      Save Dash/Child Part
                    </Button>
                  </>
                )}

                <Button color="light" className="edit-part-action-btn edit-part-utility-btn border" onClick={addBlankRow}>
                  <i className="ri-add-line edit-part-btn-icon me-1" />
                  Add Row
                </Button>

                <Button color="light" className="edit-part-action-btn edit-part-utility-btn border" onClick={copyCurrentRowToNewDNA}>
                  <i className="ri-file-copy-line edit-part-btn-icon me-1" />
                  Copy Row
                </Button>

                <Button color="danger" outline className="edit-part-action-btn edit-part-utility-btn" onClick={deleteCurrentRow} disabled={!activeCell}>
                  <i className="ri-delete-bin-6-line edit-part-btn-icon me-1" />
                  Delete Current Row
                </Button>

                <div className="edit-part-info-pill">
                  <span className="edit-part-info-label">Selected Part</span>
                  <span className="edit-part-info-value" title={normalizedPartNum || "—"}>
                    {normalizedPartNum || "—"}
                  </span>
                </div>

                <div className="edit-part-info-pill">
                  <span className="edit-part-info-label">Revision</span>
                  <span className="edit-part-info-value">{revMaj}.{revMin}</span>
                </div>

                <div className="edit-part-info-pill">
                  <span className="edit-part-info-label">Rows Loaded</span>
                  <span className="edit-part-info-value">{rows.length}</span>
                </div>

                <div className="edit-part-info-pill">
                  <span className="edit-part-info-label">Selected Cell</span>
                  <span className="edit-part-info-value">{selectedColumn || "None"}</span>
                </div>

                <div className="edit-part-info-pill">
                  <span className="edit-part-info-label">Lock Owner</span>
                  <span className="edit-part-info-value" title={lockOwner || currentUsername || "—"}>
                    {lockOwner || currentUsername || "—"}
                  </span>
                </div>
              </div>
{/* 
              {(revisedByLabel || historyLabel || errorText) && (
                <div className="edit-part-banner mt-3">
                  {revisedByLabel && <div><strong>{revisedByLabel}</strong></div>}
                  {historyLabel && <div className="mt-1">{historyLabel}</div>}
                  {errorText && <div className="mt-2 text-danger"><strong>Validation:</strong> {errorText}</div>}
                </div>
              )} */}
            </CardHeader>

            <CardBody className="p-4">
              <Row className="g-4">
                <Col xl={12}>
                  <div className="edit-part-table-shell">
                    <div className="edit-part-table-scroll">
                      <Table responsive={false} className="align-middle mb-0 edit-part-table">
                        <thead>
                          <tr>
                            <th className="edit-part-col-select">Select</th>
                            {COLUMN_DEFS.map((col) => (
                              <th key={col.key} style={{ minWidth: col.width }}>{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRowEntries.length === 0 ? (
                            <tr>
                              <td colSpan={COLUMN_DEFS.length + 1} className="text-center py-5 text-muted">
                                No rows match your search.
                              </td>
                            </tr>
                          ) : (
                            filteredRowEntries.map(({ row, index: originalIndex }, filteredIndex) => {
                              const isSelected = activeCell?.rowIndex === originalIndex;
                              return (
                                <tr
                                  key={`${originalIndex}-${row.DNA}-${filteredIndex}`}
                                  ref={(el) => {
                                    rowRefs.current[originalIndex] = el;
                                  }}
                                  className={`edit-part-data-row ${isSelected ? "selected" : ""}`}
                                  onClick={() =>
                                    setActiveCell({
                                      rowIndex: originalIndex,
                                      field: activeCell?.rowIndex === originalIndex ? activeCell.field : "DNA",
                                    })
                                  }
                                >
                                  <td className="edit-part-col-select">
                                    <Input
                                      type="radio"
                                      checked={isSelected}
                                      onChange={() => setActiveCell({ rowIndex: originalIndex, field: "DNA" })}
                                      name="selected-edit-part-row"
                                    />
                                  </td>
                                  {COLUMN_DEFS.map((col) => (
                                    <EditableCell
                                      key={col.key}
                                      rowIndex={originalIndex}
                                      row={row}
                                      column={col}
                                      gageOptions={gageOptions}
                                      onChange={setRowValue}
                                      setActiveCell={setActiveCell}
                                      inputRefs={inputRefs}
                                      className={col.key === "AdditionalInfo" ? getAdditionalInfoClass(row) : ""}
                                    />
                                  ))}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-4 mt-1">
                <Col xl={4} md={6}>
                  <div className="edit-part-panel-card h-100">
                    <h5 className="mb-3">Part Controls</h5>
                    <div className="mb-3">
                      <Label className="form-label">Part Number</Label>
                      <Input value={partText} onChange={(e) => setPartText(e.target.value)} />
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">Determine In Process Quantity</Label>
                      <Input value={qtyForSampling} onChange={(e) => setQtyForSampling(e.target.value)} />
                    </div>
                    <Button color="success" className="w-100" onClick={determineInProcess}>
                      <i className="ri-calculator-line me-1" /> Determine In Process
                    </Button>
                  </div>
                </Col>

                {!isImported && (
                  <Col xl={4} md={6}>
                    <div className="edit-part-panel-card h-100">
                      <h5 className="mb-3">Manual Rev Change</h5>
                      <Row className="g-3">
                        <Col md={6}>
                          <Label className="form-label">Rev Major</Label>
                          <Input value={manualRevMaj} onChange={(e) => setManualRevMaj(e.target.value)} />
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Rev Minor</Label>
                          <Input value={manualRevMin} onChange={(e) => setManualRevMin(e.target.value)} />
                        </Col>
                      </Row>
                      <div className="form-check mt-3">
                        <Input
                          id="keepOpenAfterSave"
                          type="checkbox"
                          checked={keepOpenAfterSave}
                          onChange={(e) => setKeepOpenAfterSave(e.target.checked)}
                        />
                        <Label htmlFor="keepOpenAfterSave" className="form-check-label">
                          Keep page open after save
                        </Label>
                      </div>
                    </div>
                  </Col>
                )}

                <Col xl={4} md={6}>
                  <div className="edit-part-panel-card h-100">
                    <h5 className="mb-3">Children</h5>
                    <div className="mb-3">
                      <Label className="form-label">Child Part</Label>
                      <Input value={childInput} onChange={(e) => setChildInput(e.target.value)} />
                    </div>
                    <div className="d-flex gap-2 mb-3">
                      <Button color="primary" className="flex-fill" onClick={addChildPart}>Add Child</Button>
                      <Button color="danger" outline className="flex-fill" onClick={removeChildPart}>Remove Child</Button>
                    </div>
                    <div className="edit-part-children-list">
                      {childParts.length === 0 ? (
                        <div className="text-muted small">No child parts.</div>
                      ) : (
                        childParts.map((child) => (
                          <div key={child} className="edit-part-child-chip">{child}</div>
                        ))
                      )}
                    </div>
                  </div>
                </Col>

                <Col xl={12}>
                  <div className="edit-part-panel-card">
                    <h5 className="mb-3">Symbols</h5>
                    <div className="edit-part-symbol-grid">
                      <SymbolButton label="/" onClick={() => insertSymbol("slash")} />
                      <SymbolButton label="(" onClick={() => insertSymbol("openParen")} />
                      <SymbolButton label=")" onClick={() => insertSymbol("closeParen")} />
                      <SymbolButton label="{" onClick={() => insertSymbol("openBrace")} />
                      <SymbolButton label="}" onClick={() => insertSymbol("closeBrace")} />
                      <SymbolButton label="<" onClick={() => insertSymbol("lessThan")} />
                      <SymbolButton label=">" onClick={() => insertSymbol("greaterThan")} />
                      <SymbolButton label="±" onClick={() => insertSymbol("plusMinus")} />
                      <SymbolButton label="°" onClick={() => insertSymbol("degree")} />
                    </div>
                    <div className="edit-part-panel-note mt-3">
                      Symbols are inserted into the currently selected editable field.
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .edit-part-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .edit-part-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .edit-part-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .edit-part-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .edit-part-search-group {
          width: 100%;
        }

        .edit-part-toolbar-card,
        .edit-part-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
          height: 100%;
        }

        .edit-part-banner {
          border: 1px solid rgba(13, 110, 253, 0.12);
          background: rgba(13, 110, 253, 0.05);
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          color: #334155;
        }

        .edit-part-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .edit-part-utility-btn {
          min-width: 125px;
        }

        .edit-part-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .edit-part-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 320px;
        }

        .edit-part-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .edit-part-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .edit-part-table-shell {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          overflow: hidden;
          background: #fff;
        }

        .edit-part-table-scroll {
          max-height: 720px;
          overflow: auto;
        }

        .edit-part-table {
          min-width: 2300px;
        }

        .edit-part-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0f172a;
          color: #f8fafc;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
          padding: 0.95rem 0.9rem;
          border-bottom: 0;
          white-space: nowrap;
        }

        .edit-part-table tbody td {
          padding: 0.65rem 0.7rem;
          border-top: 1px solid #eef2f7;
          color: #334155;
          vertical-align: middle;
          background: transparent;
        }

        .edit-part-col-select {
          width: 72px;
          text-align: center;
        }

        .edit-part-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .edit-part-data-row:hover {
          background: #f5f9ff;
        }

        .edit-part-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .edit-part-cell-input,
        .edit-part-cell-select {
          width: 100%;
          min-width: 100%;
          border: 1px solid #dbe4ee;
          border-radius: 0.65rem;
          padding: 0.5rem 0.65rem;
          font-size: 0.875rem;
          background: #fff;
          outline: none;
        }

        .edit-part-cell-input:focus,
        .edit-part-cell-select:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.2rem rgba(13,110,253,.12);
        }

        .edit-part-additional-reference {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 13px;
        }

        .edit-part-additional-note {
          font-family: inherit;
        }

        .edit-part-additional-gdt {
          font-weight: 700;
          letter-spacing: 0.04em;
        }

        .edit-part-additional-default {
          font-family: Georgia, serif;
        }

        .edit-part-symbol-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.6rem;
        }

        .edit-part-symbol-btn {
          border-radius: 0.8rem;
          border: 1px solid #dbe4ee;
          background: #fff;
          padding: 0.75rem;
          font-weight: 700;
          color: #334155;
        }

        .edit-part-symbol-btn:hover {
          background: #f8fafc;
        }

        .edit-part-children-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          min-height: 42px;
        }

        .edit-part-child-chip {
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.75rem;
          border-radius: 999px;
          background: rgba(13, 110, 253, 0.08);
          color: #0d6efd;
          font-weight: 600;
          font-size: 0.86rem;
        }

        .edit-part-panel-note {
          color: #64748b;
          font-size: 0.92rem;
        }

        @media (max-width: 1199.98px) {
          .edit-part-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .edit-part-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .edit-part-table-scroll {
            max-height: 520px;
          }

          .edit-part-symbol-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .edit-part-info-pill {
            max-width: 100%;
          }

          .edit-part-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function EditableCell({ rowIndex, row, column, gageOptions, onChange, setActiveCell, inputRefs, className = "" }) {
  const commonProps = {
    onFocus: () => setActiveCell({ rowIndex, field: column.key }),
    className: column.type === "select" ? "edit-part-cell-select" : `edit-part-cell-input ${className}`.trim(),
  };

  if (column.type === "select") {
    return (
      <td>
        <select
          value={row[column.key] ?? ""}
          onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
          {...commonProps}
        >
          <option value=""></option>
          {column.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </td>
    );
  }

  if (column.type === "datalist") {
    const listId = `gage-list-${rowIndex}`;
    const key = `${rowIndex}-${column.key}`;
    return (
      <td>
        <input
          ref={(el) => { inputRefs.current[key] = el; }}
          list={listId}
          type="text"
          value={row[column.key] ?? ""}
          onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
          {...commonProps}
        />
        <datalist id={listId}>
          {gageOptions.map((opt) => <option key={opt} value={opt} />)}
        </datalist>
      </td>
    );
  }

  const key = `${rowIndex}-${column.key}`;
  return (
    <td>
      <input
        ref={(el) => { inputRefs.current[key] = el; }}
        type="text"
        value={row[column.key] ?? ""}
        onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
        {...commonProps}
      />
    </td>
  );
}

function SymbolButton({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="edit-part-symbol-btn">
      {label}
    </button>
  );
}

function stringOrEmpty(value) {
  if (value == null) return "";
  return String(value).trim();
}

function nullableDecimalToString(value) {
  if (value == null) return "";
  return String(value);
}

function nullableApiDecimal(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "" || trimmed === "-") return 0;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? 0 : parsed;
}


function getAdditionalInfoClass(row) {
  const dimensionType = String(row.DimensionType || "").toLowerCase().trim();
  const toleranceClass = String(row.ToleranceClass || "").toLowerCase().trim();

  if (toleranceClass.includes("reference")) return "edit-part-additional-reference";
  if (dimensionType.includes("note") || dimensionType.includes("*") || dimensionType.includes("finish")) return "edit-part-additional-note";
  if (dimensionType.includes("gd&t")) return "edit-part-additional-gdt";
  return "edit-part-additional-default";
}

function encodeReferenceSymbols(value) {
  return String(value || "")
    .replaceAll("/", "∕")
    .replaceAll("-", " - ")
    .replaceAll(":", " ▪ ")
    .replaceAll("<", "Ê ")
    .replaceAll(">", "Ë")
    .replaceAll("{", "Ì ")
    .replaceAll("}", "Í")
    .replaceAll("(", "È ")
    .replaceAll(")", "É")
    .replaceAll(",", "‚");
}

function decodeReferenceSymbols(value) {
  return String(value || "")
    .replaceAll("Ê", "<")
    .replaceAll("Ë", ">")
    .replaceAll("Ì", "{")
    .replaceAll("Í", "}")
    .replaceAll("È", "(")
    .replaceAll("É", ")");
}

function getSymbolMapping(kind, isNew, isReferenceStyle) {
  const maps = {
    slash: { isNew: "/", reference: "⁄", normal: "" },
    openParen: { any: "È" },
    closeParen: { any: "É" },
    openBrace: { any: "Ì" },
    closeBrace: { any: "Í" },
    lessThan: { any: "Ê" },
    greaterThan: { any: "Ë" },
    plusMinus: { any: "±" },
    degree: { any: "°" },
  };

  const m = maps[kind];
  if (!m) return { allowed: false, value: "" };
  if (m.any) {
    if (isNew) return { allowed: true, value: m.any };
    return isReferenceStyle ? { allowed: true, value: m.any } : { allowed: false, value: "" };
  }
  if (kind === "slash") {
    if (isNew) return { allowed: true, value: m.isNew };
    if (isReferenceStyle) return { allowed: true, value: m.reference };
    return { allowed: true, value: m.normal };
  }
  return { allowed: false, value: "" };
}

async function getCurrentUsername() {
  return "john.doe";
}

async function getLockStatus(request) {
  const response = await api.post("mafia/lockstatus", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function setLockStatus(request) {
  const response = await api.post("mafia/set-lock-status", {
    partNumber: request.partNum,
    rev: request.rev,
    revMinor: request.revMinor,
    newLock: request.newLock,
  });
  return response.data;
}

async function getOldHistory(request) {
  const response = await api.post("mafia/old-history", request);
  return response.data;
}

async function getRevisedBy(request) {
  const response = await api.post("mafia/revisedby", request);
  return response.data;
}

async function getCreatedBy(request) {
  const response = await api.post("mafia/createdby", request);
  return response.data;
}

async function getPart(request) {
  const response = await api.post("mafia/part", request);
  return response.data;
}

async function getGageList() {
  const response = await api.get("mafia/gagelist");
  return response.data;
}

async function getChildren(partnum) {
  const response = await api.get("mafia/children", { params: { partnum } });
  return response.data;
}

async function determineMultiplier(dimType, toleranceClass, jobQuan) {
  const response = await api.post("mafia/determine-multiplier", null, {
    params: { dimType, toleranceClass, jobQuan }
  });
  return response.data;
}

async function getPartExistsMajor(partnum) {
  const response = await api.get("mafia/part-exists", { params: { partnum } });
  return response.data;
}

async function getPartExistsExact(request) {
  const response = await api.post("mafia/part-exists", request);
  return response.data;
}

async function addLine(payload) {
  const response = await api.post("mafia/add-line", payload);
  return response.data;
}

async function addLineUnfinished(payload) {
  const response = await api.post("mafia/add-line-unfinished", payload);
  return response.data;
}

async function deleteMafiaTable(partnum, revMajor, revMinor) {
  const response = await api.delete("mafia/deletemafiatable", {
    params: { partnum, revMajor, revMinor }
  });
  return response.data;
}

async function updateRevMinor(request) {
  const response = await api.post("mafia/update-revminor", request);
  return response.data;
}

async function addChildPartApi(request) {
  const response = await api.post("mafia/add-child-part", request);
  return response.data;
}

async function deleteChild(partnum) {
  const response = await api.delete("mafia/delete-children", { params: { partnum } });
  return response.data;
}

async function sendEmailPartEdited(request) {
  const response = await api.post("mafia/email-part-edited", request);
  return response.data;
}

async function getLatestMajorRev(partnum) {
  const response = await api.get("mafia/latest-major-rev", { params: { partnum } });
  return response.data;
}

async function getLatestRevMinor(request) {
  const response = await api.post("mafia/latest-revminor", request);
  return response.data;
}